import { useState, useEffect } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import SignIn from "./pages/SignIn.tsx";
import LeadsPage from "./pages/LeadsPage.tsx";
import Dashboard from "./pages/Dashboard.tsx";
import ConnectFub from "./pages/ConnectFub.tsx";
import CalendarCallback from "./pages/CalendarCallback.tsx";
import AdminGoogleCalendarConnect from "./pages/AdminGoogleCalendarConnect.tsx";
import ResetPasswordForm from "./components/ResetPasswordForm.tsx";
import RootWrapper from "./components/RootWrapper.tsx";
import SubscriptionOverlay from "./components/SubscriptionOverlay.tsx";
import ProtectedRoute from "./utils/ProtectedRoute.js";
import FubConnectionGuard from "./components/FubConnectionGuard.tsx";
import { store } from "./store";
import { fetchUserProfile } from "./store/authSlice";
import { useTokenRefresh } from "./hooks/useTokenRefresh";

function AppContent() {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [profileSettingsIsOpen, setProfileSettingsIsOpen] = useState(false);

  // Fetch user profile when authenticated
  useEffect(() => {
    if (isAuthenticated && !user) {
      dispatch(fetchUserProfile());
    }
  }, [isAuthenticated, user, dispatch]);
  
  return (
    <Router>
      <AppWithTokenRefresh 
        user={user}
        isAuthenticated={isAuthenticated}
        profileSettingsIsOpen={profileSettingsIsOpen}
        setProfileSettingsIsOpen={setProfileSettingsIsOpen}
      />
    </Router>
  );
}

function AppWithTokenRefresh({ user, isAuthenticated, profileSettingsIsOpen, setProfileSettingsIsOpen }) {
  // Initialize token refresh monitoring for authenticated users
  useTokenRefresh();

  return (
    <RootWrapper>
      <div className="my-homey-app">
        <Routes>
          <Route path="/sign_in" element={<SignIn />} />
          <Route path="/reset-password" element={<ResetPasswordForm />} />
          <Route
            path="/auth/google/callback"
            element={<CalendarCallback />}
          />
          <Route
            path="/admin/google-calendar"
            element={<AdminGoogleCalendarConnect />}
          />
          <Route
            path="/connect-fub"
            element={
              <ProtectedRoute>
                <ConnectFub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads"
            element={
              <ProtectedRoute>
                <FubConnectionGuard>
                  <LeadsPage
                    user={user}
                    profileSettingsIsOpen={profileSettingsIsOpen}
                    setProfileSettingsIsOpen={setProfileSettingsIsOpen}
                  />
                </FubConnectionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <FubConnectionGuard>
                  <Dashboard
                    user={user}
                    profileSettingsIsOpen={profileSettingsIsOpen}
                    setProfileSettingsIsOpen={setProfileSettingsIsOpen}
                  />
                </FubConnectionGuard>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/leads" replace />} />
        </Routes>
        {/* Global subscription overlay (blurs everything except header when inactive) */}
        <SubscriptionOverlay />
      </div>
    </RootWrapper>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
